import Uppy from '@uppy/core';
import {compressFiles} from '@/services/image-compression.service';
import {getImageProcessingConcurrency, validateImageFile} from '@/services/image-validation.service';
import {mapWithConcurrency} from '@/services/concurrency';

export const DEFAULT_TILE_UPLOAD_BATCH_SIZE = 8;

function splitIntoBatches(fileIDs, batchSize) {
    const batches = [];
    for(let offset = 0; offset < fileIDs.length; offset += batchSize) {
        batches.push(fileIDs.slice(offset, offset + batchSize));
    }
    return batches;
}

function normalizeBatch(batch) {
    return Array.isArray(batch) ? batch : [];
}

export class TileUppy extends Uppy {
    #uploaders = new Map();
    #batchSize;
    #prepareBatch;
    #onBatchStart;
    #onBatchComplete;

    constructor(options = {}, pipelineOptions = {}) {
        super(options);
        this.#batchSize = Math.max(1, Number(pipelineOptions.batchSize) || DEFAULT_TILE_UPLOAD_BATCH_SIZE);
        this.#prepareBatch = pipelineOptions.prepareBatch || (fileIDs => fileIDs);
        this.#onBatchStart = pipelineOptions.onBatchStart || null;
        this.#onBatchComplete = pipelineOptions.onBatchComplete || null;
    }

    addUploader(uploader) {
        const wrappedUploader = async (fileIDs, uploadID) => {
            const batches = splitIntoBatches(fileIDs, this.#batchSize);
            if(batches.length === 0) {
                return;
            }

            const prepare = (batch, index) => Promise.resolve()
                .then(() => this.#prepareBatch(batch, index, batches.length, uploadID))
                .then(value => ({value}), error => ({error}));
            const isActive = () => Boolean(this.getState().currentUploads[uploadID]);
            let prepared = prepare(batches[0], 0);

            for(let index = 0; index < batches.length; index++) {
                const preparedResult = await prepared;
                if(preparedResult.error) {
                    if(!isActive()) {
                        return;
                    }
                    throw preparedResult.error;
                }
                if(!isActive()) {
                    return;
                }

                const batch = normalizeBatch(preparedResult.value)
                    .filter(fileID => this.getFile(fileID));
                const nextBatch = batches[index + 1];
                let nextPrepared = null;
                if(nextBatch) {
                    nextPrepared = prepare(nextBatch, index + 1);
                }

                if(batch.length === 0) {
                    prepared = nextPrepared;
                    continue;
                }

                try {
                    if(this.#onBatchStart) {
                        await this.#onBatchStart(batch, index, batches.length, uploadID);
                    }
                    if(isActive()) {
                        await uploader(batch, uploadID);
                    }
                } catch(error) {
                    if(nextPrepared) {
                        await nextPrepared;
                    }
                    throw error;
                }
                if(this.#onBatchComplete && isActive()) {
                    await this.#onBatchComplete(batch, index, batches.length, uploadID);
                }
                prepared = nextPrepared;
            }
        };

        this.#uploaders.set(uploader, wrappedUploader);
        return super.addUploader(wrappedUploader);
    }

    removeUploader(uploader) {
        const wrappedUploader = this.#uploaders.get(uploader);
        if(!wrappedUploader) {
            return super.removeUploader(uploader);
        }

        this.#uploaders.delete(uploader);
        return super.removeUploader(wrappedUploader);
    }
}

export function createTileBatchPreparer(uppy, {
    compressionOptions,
    onValidationError = null,
    onCompressionError = null,
    onPrepared = null,
    validate = validateImageFile
} = {}) {
    return async (fileIDs, index, total, uploadID) => {
        const validationStartedAt = Date.now();
        const results = await mapWithConcurrency(fileIDs, getImageProcessingConcurrency(), async fileID => {
            const file = uppy.getFile(fileID);
            if(!file) {
                return {fileID, file: null, errorKey: 'image_validation_file'};
            }

            try {
                return {fileID, file, errorKey: await validate(file)};
            } catch(error) {
                return {fileID, file, errorKey: 'image_validation_file'};
            }
        });
        const validIDs = [];

        results.forEach(result => {
            if(result.errorKey) {
                if(result.file && uppy.getFile(result.fileID)) {
                    if(!onValidationError) {
                        uppy.setFileState(result.fileID, {error: result.errorKey});
                    }
                }
                if(onValidationError && result.file && uppy.getFile(result.fileID)) {
                    onValidationError(result.file, result.errorKey);
                }
                return;
            }
            validIDs.push(result.fileID);
        });

        const validationMs = Date.now() - validationStartedAt;
        const compressionStartedAt = Date.now();
        if(compressionOptions && validIDs.length > 0) {
            await compressFiles(uppy, validIDs, compressionOptions, onCompressionError);
        }
        if(onPrepared) {
            onPrepared({
                count: fileIDs.length,
                valid: validIDs.length,
                rejected: fileIDs.length - validIDs.length,
                validation_ms: validationMs,
                compression_ms: Date.now() - compressionStartedAt
            }, {batch: index, total, uploadID});
        }

        return validIDs;
    };
}
